import React, { useMemo, useState } from "react";
import { RouterProvider, createMemoryHistory } from "@tanstack/react-router";
import { NavigationProvider } from "@archery-inc/design-system";
import {
  AppConfigContext,
  AppLayoutComponent,
  AppRouterContext,
  createAppRouter,
} from "./router";

interface AppProps {
  forceLogin?: boolean;
  layout?: AppRouterContext["layout"];
  /** Renders on an in-memory history starting at this path (tests). */
  initialPath?: string;
}

export type { AppLayoutComponent };

export default function App({ layout, forceLogin, initialPath }: AppProps) {
  const [router] = useState(() =>
    createAppRouter({
      history: initialPath
        ? createMemoryHistory({ initialEntries: [initialPath] })
        : undefined,
    }),
  );
  const config = useMemo<Partial<AppRouterContext>>(
    () => ({ layout, forceLogin }),
    [layout, forceLogin],
  );
  return (
    <AppConfigProvider config={config}>
      <NavigationProvider
        navigate={(href) => {
          void router.history.push(href);
        }}
      >
        <RouterProvider router={router} />
      </NavigationProvider>
    </AppConfigProvider>
  );
}

function AppConfigProvider({
  config,
  children,
}: {
  config: Partial<AppRouterContext>;
  children: React.ReactNode;
}) {
  const defaults = React.useContext(AppConfigContext);
  const value = useMemo(
    () => ({
      layout: config.layout ?? defaults.layout,
      forceLogin: config.forceLogin ?? defaults.forceLogin,
    }),
    [config, defaults],
  );
  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}
